/**
 * Cinematic Hook Registry
 *
 * Registry for cinematic event handlers.
 * Hooks are invoked by CinematicManager at specific normalized times.
 */

import type { CinematicHookFn, CinematicHookEntry, CinematicHookAPI } from '../types/cinematic';
import { logger } from '../../core/Logger';

class CinematicHookRegistryImpl {
  private hooks: Map<string, CinematicHookEntry> = new Map();

  /**
   * Register a cinematic hook
   */
  register(id: string, displayName: string, handler: CinematicHookFn): void {
    if (this.hooks.has(id)) {
      logger.core.warn(`CinematicHookRegistry: Overwriting existing hook "${id}"`);
    }

    this.hooks.set(id, { id, displayName, handler });
    logger.core.debug(`CinematicHookRegistry: Registered hook "${id}"`);
  }

  /**
   * Invoke a hook by ID
   */
  invoke(hookId: string, api: CinematicHookAPI, params: Record<string, unknown>): boolean {
    const entry = this.hooks.get(hookId);
    if (!entry) {
      logger.core.warn(`CinematicHookRegistry: Unknown hook "${hookId}"`);
      return false;
    }

    try {
      entry.handler(api, params);
      return true;
    } catch (error) {
      logger.core.error(`CinematicHookRegistry: Error invoking hook "${hookId}":`, error);
      return false;
    }
  }

  /**
   * Check if a hook is registered
   */
  has(hookId: string): boolean {
    return this.hooks.has(hookId);
  }

  /**
   * Get all registered hook IDs
   */
  getRegisteredHooks(): string[] {
    return Array.from(this.hooks.keys());
  }

  /**
   * Clear all hooks (for testing)
   */
  clear(): void {
    this.hooks.clear();
  }
}

// Singleton instance
export const CinematicHookRegistry = new CinematicHookRegistryImpl();
